// regular import of dd classes breaks the datadog logging, you can only import types!!
import type { LogsPublicApi } from "@datadog/browser-logs/src/boot/logsPublicApi";
import type { RumPublicApi } from "@datadog/browser-rum-core/cjs/boot/rumPublicApi";

declare global {
    interface Window {
        DD_LOGS: LogsPublicApi;
        DD_RUM: RumPublicApi;
    }
}

const expectedErrors = new RegExp(
    "Request failed with status code 401|Request failed with status code 410|Request failed with status code 409",
    "mi",
);
/* eslint-enable prefer-regex-literals */

const ignoreErrors = [
    /Can't find variable: instantSearchSDKJSBridgeClearHighlight/, // https://stackoverflow.com/questions/69261499/what-is-instantsearchsdkjsbridgeclearhighlight
    /^No error$/,
    /__show__deepen/,
    /_avast_submit/,
    /Access is denied/,
    /anonymous function: captureException/,
    /Blocked a frame with origin/,
    /can't redefine non-configurable property "userAgent"/,
    /change_ua/,
    /console is not defined/,
    /cordova/,
    /DataCloneError/,
    /Error: AccessDeny/,
    /event is not defined/,
    /feedConf/,
    /ibFindAllVideos/,
    /myGloFrameList/,
    /SecurityError/,
    /MyIPhoneApp/,
    /snapchat.com/,
    /vid_mate_check is not defined/,
    /win\.document\.body/,
    /window\._sharedData\.entry_data/,
    /window\.regainData/,
    /ztePageScrollModule/, // https://gist.github.com/pioug/b006c983538538066ea871d299d8e8bc
    /Network Error/,
    /The network connection was lost/,
    /Could not connect to the server/,
    /XHR error POST https:\/\/.*?\.clarity\.ms\/collect/, // Don't log errors where tracking is being blocked
    /XHR error POST https:\/\/stats\.g\.doubleclick\.net\/j\/collect.*/,
    /XHR error POST https:\/\/bat\.bing\.com\/p\/insights\/c\/.*/,
    /XHR error POST https:\/\/www\.google-analytics\.com\/j\/collect.*/,
    /XHR error POST https:\/\/.\.trackedweb\.net\/cartInsight./,
    expectedErrors,
];

window.DD_LOGS?.onReady(function () {
    window.DD_LOGS.init({
        clientToken: process.env.REACT_DD_CLIENT_TOKEN ?? "",
        site: "datadoghq.eu",
        service: "website-ui",
        env: process.env.REACT_DD_ENV,
        version: process.env.REACT_DD_VERSION,
        forwardErrorsToLogs: true,
        forwardConsoleLogs: process.env.REACT_DD_ENV === "prd" ? ["error"] : ["error", "warn"],
        sessionSampleRate: 0,
        storeContextsAcrossPages: false,
        trackSessionAcrossSubdomains: true,
        useSecureSessionCookie: true,

        beforeSend: (log) => {
            // log.error.config.url = log.view.url.replace(/email=[^&]*/, "email=REDACTED")
            if (
                (log.http && [404].includes(log.http.status_code)) ||
                ignoreErrors.some((rx) => rx.test(log.message))
            ) {
                return false;
            }
            return true;
        },
    });

    //window.DD_LOGS.setGlobalContextProperty("Team", "web");
    window.DD_LOGS.setGlobalContextProperty("Properties.Team", "web");

    window.DD_LOGS.logger.setLevel("info");

    if (process.env.REACT_DD_ENV === "local") {
        // Pass all DataDog logs to Console in Development mode
        window.DD_LOGS.logger.setHandler("console");
        window.DD_LOGS.logger.setLevel("debug");
    }

    window.DD_LOGS.createLogger("PaymentRequest", {
        level: "info",
        handler: process.env.REACT_DD_ENV === "prd" ? ["http"] : ["console", "http"],
    });

    window.DD_LOGS.createLogger("React", {
        level: "info",
        handler: process.env.REACT_DD_ENV === "prd" ? ["http"] : ["console", "http"],
    });

    window.DD_LOGS.createLogger("Checkout", {
        level: process.env.REACT_DD_ENV === "prd" ? "info" : "debug",
        handler: process.env.REACT_DD_ENV === "prd" ? ["http"] : ["console", "http"],
    });

    window.DD_LOGS.createLogger("Mup", {
        level: "info",
        handler: process.env.REACT_DD_ENV === "prd" ? ["http"] : ["console", "http"],
    });

    window.DD_LOGS.createLogger("Bing", {
        level: "info",
        handler: process.env.REACT_DD_ENV === "prd" ? ["http"] : ["console", "http"],
    });

    window.DD_LOGS.createLogger("Certona", {
        level: process.env.REACT_DD_ENV === "prd" ? "warn" : "info",
        handler: process.env.REACT_DD_ENV === "prd" ? ["http"] : ["console", "http"],
    });

    window.DD_LOGS.createLogger("Basket", {
        level: process.env.REACT_DD_ENV === "prd" ? "warn" : "info",
        handler: process.env.REACT_DD_ENV === "prd" ? ["http"] : ["console", "http"],
    });

    window.DD_LOGS.logger.debug("Initialized in Layout!");
});

window.DD_RUM?.onReady(function () {
    window.DD_RUM.init({
        clientToken: process.env.REACT_DD_CLIENT_TOKEN ?? "",
        applicationId: "dcb59b6b-0e67-4cb1-92ee-a636e60cdc07",
        site: "datadoghq.eu",
        service: "website-ui",
        env: process.env.REACT_DD_ENV,
        version: process.env.REACT_DD_VERSION,
        sessionSampleRate: Number.parseInt(process.env.REACT_DD_RUM_SAMPLE_RATE ?? "1"), // 1% in Production, 50% in Alpha, 0% in Local // Defaults to 1%
        sessionReplaySampleRate: 0, // Disable Reply
        trackResources: true,
        trackLongTasks: true,
        trackUserInteractions: true,
        defaultPrivacyLevel: "mask-user-input",
        enableExperimentalFeatures: ["feature_flags"],
        allowedTracingUrls: [
            // "https://www.mountainwarehouse.com",
            /https:\/\/.*\.mountainwarehouse\.com/,
            // (url) => url.startsWith("https://www.mountainwarehouse.com"),
        ],
        traceSampleRate: 100, // If a session is sampled, then trace all of it's API requests
        trackSessionAcrossSubdomains: true,
        trackViewsManually: true,
    });

    window.DD_RUM.setGlobalContextProperty("Team", "web");
});
